import React, { useState, useEffect } from 'react';
import api, { channelAPI, Channel } from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface ChannelFormData {
  name: string;
  code: string;
  type: string;
  config: Record<string, any>;
  is_active: boolean;
  default_length: string;
  default_width: string;
  default_height: string;
  default_weight: string;
  default_declared_value: string;
  auto_allocate: boolean;
}

const CHANNEL_TYPES = ['ecommerce', 'marketplace', 'retail', 'custom'];

const emptyForm: ChannelFormData = {
  name: '',
  code: '',
  type: 'ecommerce',
  config: {},
  is_active: true,
  default_length: '',
  default_width: '',
  default_height: '',
  default_weight: '',
  default_declared_value: '',
  auto_allocate: false,
};

const typeBadgeColors: Record<string, { bg: string; color: string }> = {
  ecommerce: { bg: '#dbeafe', color: '#1e40af' },
  marketplace: { bg: '#fef3c7', color: '#92400e' },
  retail: { bg: '#d1fae5', color: '#065f46' },
  custom: { bg: '#ede9fe', color: '#5b21b6' },
};

interface ChannelManagerProps {
  userRole?: string;
}

const ChannelManager: React.FC<ChannelManagerProps> = ({ userRole = 'viewer' }) => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [formData, setFormData] = useState<ChannelFormData>(emptyForm);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // SKU Mapping state
  const [viewingMappings, setViewingMappings] = useState<Channel | null>(null);
  const [mappings, setMappings] = useState<Array<{id: number; product_id: number; nexus_sku: string; product_name: string; channel_sku: string; channel_price: number | null; is_published: boolean}>>([]);
  const [mappingSearch, setMappingSearch] = useState('');
  const [mappingLoading, setMappingLoading] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [newMappingProductSearch, setNewMappingProductSearch] = useState('');
  const [newMappingResults, setNewMappingResults] = useState<Array<{id: number; sku: string; name: string; attributes?: Array<{attribute_name: string; value: string}>}>>([]);
  const [newMappingProduct, setNewMappingProduct] = useState<{id: number; sku: string; name: string; attributes?: Array<{attribute_name: string; value: string}>} | null>(null);
  const [newMappingChannelSku, setNewMappingChannelSku] = useState('');
  const [newMappingPrice, setNewMappingPrice] = useState('');

  // Import Template state
  const [viewingTemplate, setViewingTemplate] = useState<Channel | null>(null);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [templateColumns, setTemplateColumns] = useState<string[]>([]);
  const [templateMapping, setTemplateMapping] = useState<Record<string, string>>({});
  const [templatePreview, setTemplatePreview] = useState<any[]>([]);

  // Integration Config state
  const [viewingIntegration, setViewingIntegration] = useState<Channel | null>(null);
  const [integrationConfig, setIntegrationConfig] = useState<any>(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationSaving, setIntegrationSaving] = useState(false);
  const [integrationForm, setIntegrationForm] = useState({
    api_base_url: '',
    email: '',
    password: '',
    program_ids: '',
    sync_enabled: false,
    order_sync_schedule: 'hourly',
    status_push_schedule: 'every_3h',
    sync_start_hour: 6,
    sync_end_hour: 23,
    order_sync_from_date: '',
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{success: boolean; message: string; programs?: any[]} | null>(null);
  const [syncLogs, setSyncLogs] = useState<any[]>([]);
  const [manualSyncing, setManualSyncing] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [removeMappingConfirm, setRemoveMappingConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [templateMsg, setTemplateMsg] = useState<string | null>(null);

  const isAdmin = userRole === 'admin';

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const res = await channelAPI.listChannels();
      setChannels(res.data);
    } catch (error) {
      console.error('Error fetching channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingChannel(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleOpenEdit = (channel: Channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      code: (channel as any).code || '',
      type: channel.type,
      config: channel.config || {},
      is_active: channel.is_active,
      default_length: (channel as any).default_length?.toString() || '',
      default_width: (channel as any).default_width?.toString() || '',
      default_height: (channel as any).default_height?.toString() || '',
      default_weight: (channel as any).default_weight?.toString() || '',
      default_declared_value: (channel as any).default_declared_value?.toString() || '',
      auto_allocate: (channel as any).auto_allocate || false,
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingChannel(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a channel name');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        ...formData,
        default_length: formData.default_length ? parseFloat(formData.default_length) : null,
        default_width: formData.default_width ? parseFloat(formData.default_width) : null,
        default_height: formData.default_height ? parseFloat(formData.default_height) : null,
        default_weight: formData.default_weight ? parseFloat(formData.default_weight) : null,
        default_declared_value: formData.default_declared_value ? parseFloat(formData.default_declared_value) : null,
      };
      if (editingChannel) {
        await channelAPI.updateChannel(editingChannel.id, payload);
      } else {
        await channelAPI.createChannel(payload);
      }
      handleCloseForm();
      fetchChannels();
    } catch (error: any) {
      console.error('Error saving channel:', error);
      alert(error.response?.data?.detail || 'Failed to save channel');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (channel: Channel) => {
    try {
      await channelAPI.updateChannel(channel.id, { is_active: !channel.is_active });
      fetchChannels();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to update channel status');
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (confirmText !== 'confirm' || !deleteId) return;
    try {
      await channelAPI.deleteChannel(deleteId);
      fetchChannels();
      setShowDeleteConfirm(false);
      setDeleteId(null);
      setConfirmText('');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to delete channel');
    }
  };

  const getTypeBadge = (type: string) => {
    const colors = typeBadgeColors[type] || { bg: '#f1f5f9', color: '#475569' };
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        background: colors.bg,
        color: colors.color,
        textTransform: 'capitalize',
      }}>
        {type}
      </span>
    );
  };

  const fetchMappings = async (channelId: number, search?: string) => {
    setMappingLoading(true);
    try {
      const params: any = {};
      if (search) params.q = search;
      const res = await api.get(`/channels/${channelId}/sku-mappings`, { params });
      setMappings(res.data || []);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    } finally {
      setMappingLoading(false);
    }
  };

  const handleViewMappings = (channel: Channel) => {
    setViewingMappings(channel);
    fetchMappings(channel.id);
  };

  // Integration Config handlers
  const handleOpenIntegration = async (channel: Channel) => {
    setViewingIntegration(channel);
    setIntegrationLoading(true);
    setTestResult(null);
    try {
      const res = await api.get(`/channels/sync/${channel.id}/config`);
      setIntegrationConfig(res.data);
      setIntegrationForm({
        api_base_url: res.data.api_base_url || '',
        email: '',
        password: '',
        program_ids: (res.data.program_ids || []).join(', '),
        sync_enabled: res.data.sync_enabled || false,
        order_sync_schedule: res.data.order_sync_schedule || 'hourly',
        status_push_schedule: res.data.status_push_schedule || 'every_3h',
        sync_start_hour: res.data.sync_start_hour ?? 6,
        sync_end_hour: res.data.sync_end_hour ?? 23,
        order_sync_from_date: res.data.order_sync_from_date ? res.data.order_sync_from_date.split('T')[0] : '',
      });
      // Fetch sync logs
      const logsRes = await api.get(`/channels/sync/${channel.id}/logs`, { params: { limit: 10 } });
      setSyncLogs(logsRes.data || []);
    } catch (e) {
      console.error('Failed to load integration config:', e);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleSaveIntegration = async () => {
    if (!viewingIntegration) return;
    setIntegrationSaving(true);
    try {
      const payload: any = {
        api_base_url: integrationForm.api_base_url || null,
        sync_enabled: integrationForm.sync_enabled,
        order_sync_schedule: integrationForm.order_sync_schedule,
        status_push_schedule: integrationForm.status_push_schedule,
        sync_start_hour: integrationForm.sync_start_hour,
        sync_end_hour: integrationForm.sync_end_hour,
        order_sync_from_date: integrationForm.order_sync_from_date || null,
      };
      if (integrationForm.email && integrationForm.password) {
        payload.email = integrationForm.email;
        payload.password = integrationForm.password;
      }
      if (integrationForm.program_ids.trim()) {
        payload.program_ids = integrationForm.program_ids.split(',').map((s: string) => s.trim()).filter(Boolean);
      } else {
        payload.program_ids = null;
      }
      await api.put(`/channels/sync/${viewingIntegration.id}/config`, payload);
      alert('Integration config saved successfully');
      // Refresh config
      const res = await api.get(`/channels/sync/${viewingIntegration.id}/config`);
      setIntegrationConfig(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to save integration config');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!viewingIntegration) return;
    setTestingConnection(true);
    setTestResult(null);
    try {
      const res = await api.post(`/channels/sync/${viewingIntegration.id}/test-connection`);
      setTestResult(res.data);
    } catch (e: any) {
      setTestResult({ success: false, message: e.response?.data?.detail || 'Connection test failed' });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleManualSync = async (type: 'import' | 'push') => {
    if (!viewingIntegration) return;
    setManualSyncing(true);
    try {
      const endpoint = type === 'import' ? 'import-orders' : 'push-statuses';
      const res = await api.post(`/channels/sync/${viewingIntegration.id}/${endpoint}`);
      alert(JSON.stringify(res.data, null, 2));
      // Refresh logs
      const logsRes = await api.get(`/channels/sync/${viewingIntegration.id}/logs`, { params: { limit: 10 } });
      setSyncLogs(logsRes.data || []);
      // Refresh config (for last sync status)
      const configRes = await api.get(`/channels/sync/${viewingIntegration.id}/config`);
      setIntegrationConfig(configRes.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || `Failed to ${type === 'import' ? 'import orders' : 'push statuses'}`);
    } finally {
      setManualSyncing(false);
    }
  };

  const handleResetSync = async () => {
    if (!viewingIntegration) return;
    if (!window.confirm('Reset sync state? Next sync will start fresh from the configured "Order Sync From Date".')) return;
    try {
      const res = await api.post(`/channels/sync/${viewingIntegration.id}/reset-sync`);
      alert(res.data?.message || 'Sync state reset');
      const configRes = await api.get(`/channels/sync/${viewingIntegration.id}/config`);
      setIntegrationConfig(configRes.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to reset sync state');
    }
  };

  // Import Template functions
  const TEMPLATE_FIELDS = [
    { key: 'order_id', label: 'Order ID (External)' },
    { key: 'customer_name', label: 'Customer Name *' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'address1', label: 'Address Line 1 *' },
    { key: 'address2', label: 'Address Line 2' },
    { key: 'city', label: 'City *' },
    { key: 'state', label: 'State' },
    { key: 'pincode', label: 'Pincode *' },
    { key: 'country', label: 'Country' },
    { key: 'product_name', label: 'Product / SKU *' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'unit_price', label: 'Unit Price' },
    { key: 'order_status', label: 'Order Status' },
    { key: 'order_date', label: 'Order Date' },
    { key: 'notes', label: 'Notes' },
  ];

  const handleOpenTemplate = (channel: Channel) => {
    setViewingTemplate(channel);
    setTemplateFile(null);
    setTemplateColumns([]);
    setTemplatePreview([]);
    setTemplateMsg(null);
    // Load existing mapping if available
    const config = (channel as any).order_template_config;
    if (config && config.columns) {
      setTemplateMapping(config.columns);
    } else {
      setTemplateMapping({});
    }
  };

  const handleTemplateFileUpload = async (file: File) => {
    setTemplateFile(file);
    setTemplateMsg(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/orders/import/detect-columns', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTemplateColumns(res.data.columns || []);
      setTemplatePreview(res.data.preview || []);
      // Auto-suggest mapping but don't overwrite existing saved mapping
      const existing = (viewingTemplate as any)?.order_template_config?.columns || {};
      if (Object.keys(existing).length > 0) {
        setTemplateMapping(existing);
      } else {
        setTemplateMapping(res.data.auto_mapping || {});
      }
    } catch (err: any) {
      setTemplateMsg('Failed to detect columns: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveTemplate = async () => {
    if (!viewingTemplate) return;
    setTemplateSaving(true);
    setTemplateMsg(null);
    try {
      const config = {
        format: 'csv',
        columns: templateMapping,
        skip_comment_lines: true,
      };
      await channelAPI.updateChannel(viewingTemplate.id, {
        order_template_config: config,
      });
      setTemplateMsg('Template saved successfully!');
      fetchChannels();
    } catch (err: any) {
      setTemplateMsg('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleAddMapping = async () => {
    if (!newMappingProduct || !viewingMappings) return;
    try {
      await api.post(`/products/${newMappingProduct.id}/channels`, {
        channel_id: viewingMappings.id,
        channel_sku: newMappingChannelSku || newMappingProduct.sku,
        channel_price: newMappingPrice ? parseFloat(newMappingPrice) : null,
      });
      setShowAddMapping(false);
      setNewMappingProduct(null);
      setNewMappingProductSearch('');
      setNewMappingChannelSku('');
      setNewMappingPrice('');
      setNewMappingResults([]);
      fetchMappings(viewingMappings.id, mappingSearch);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to add mapping');
    }
  };

  const handleRemoveMapping = async (productId: number) => {
    if (!viewingMappings) return;
    if (!removeMappingConfirm.id) return;
    try {
      await api.delete(`/products/${removeMappingConfirm.id}/channels/${viewingMappings.id}`);
      setRemoveMappingConfirm({show: false, id: null, name: ''});
      fetchMappings(viewingMappings.id, mappingSearch);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to remove mapping');
    }
  };

  const handleRemoveMappingClick = (productId: number, sku: string) => {
    setRemoveMappingConfirm({show: true, id: productId, name: sku});
  };

  const [pushingProductId, setPushingProductId] = useState<number | null>(null);

  const handlePushProduct = async (productId: number) => {
    if (!viewingMappings) return;
    // Get program ID — use first configured program or prompt
    let programId = '';
    try {
      const configRes = await api.get(`/channels/sync/${viewingMappings.id}/config`);
      const programIds = configRes.data?.program_ids;
      if (programIds && programIds.length > 0) {
        programId = programIds[0];
      } else {
        const input = window.prompt('Enter the target Program ID on the channel platform:');
        if (!input) return;
        programId = input.trim();
      }
    } catch {
      const input = window.prompt('Enter the target Program ID on the channel platform:');
      if (!input) return;
      programId = input.trim();
    }

    if (!programId) return;
    setPushingProductId(productId);
    try {
      const res = await api.post(`/channels/sync/${viewingMappings.id}/push-product`, {
        product_id: productId,
        program_id: programId,
      });
      alert(res.data?.message || 'Product pushed successfully');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to push product to channel');
    } finally {
      setPushingProductId(null);
    }
  };

  const searchProductsForMapping = async (q: string) => {
    if (q.length < 2) { setNewMappingResults([]); return; }
    try {
      const res = await api.get('/products/', { params: { q, limit: 10 } });
      setNewMappingResults(res.data.products || []);
    } catch (e) { setNewMappingResults([]); }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading channels...</div>;
  }

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedChannels = [...channels].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (sortField === 'is_active') {
      aVal = aVal ? 1 : 0;
      bVal = bVal ? 1 : 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Import Template View
  if (viewingTemplate) {
    return (
      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setViewingTemplate(null)}>← Back</button>
            <h2 style={{ margin: 0, fontSize: '16px' }}>Import Template — {viewingTemplate.name} <span style={{ fontFamily: 'monospace', color: '#64748b', fontSize: '14px' }}>({(viewingTemplate as any).code})</span></h2>
          </div>
        </div>

        {/* Upload sample file */}
        <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Upload a sample file to detect columns:</label>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={e => { if (e.target.files?.[0]) handleTemplateFileUpload(e.target.files[0]); }} style={{ fontSize: '13px' }} />
          {templateColumns.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#16a34a' }}>✓ {templateColumns.length} columns detected</div>
          )}
        </div>

        {/* Preview */}
        {templatePreview.length > 0 && (
          <div style={{ marginBottom: '1rem', overflowX: 'auto' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: '#64748b' }}>Preview (first 3 rows):</div>
            <table style={{ fontSize: '11px', borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{templateColumns.map(col => <th key={col} style={{ padding: '4px 8px', background: '#f1f5f9', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{col}</th>)}</tr></thead>
              <tbody>{templatePreview.map((row, i) => <tr key={i}>{templateColumns.map(col => <td key={col} style={{ padding: '4px 8px', border: '1px solid #e2e8f0', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row[col] || ''}</td>)}</tr>)}</tbody>
            </table>
          </div>
        )}

        {/* Field Mapping */}
        <div style={{ padding: '1rem', background: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4', marginBottom: '1rem' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#0d9488' }}>Map File Columns to Order Fields</h4>
          <div className="form-grid-2" style={{ gap: '6px', marginBottom: 0 }}>
            {TEMPLATE_FIELDS.map(field => (
              <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                <span style={{ flex: 1, fontSize: '12px', fontWeight: 600 }}>{field.label}</span>
                <select
                  value={templateMapping[field.key] || ''}
                  onChange={e => setTemplateMapping({ ...templateMapping, [field.key]: e.target.value })}
                  style={{ width: '160px', padding: '3px 6px', border: '1px solid #dde2e8', borderRadius: '4px', fontSize: '12px' }}
                >
                  <option value="">— skip —</option>
                  {templateColumns.map(col => <option key={col} value={col}>{col}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Save */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={handleSaveTemplate} disabled={templateSaving || Object.values(templateMapping).filter(Boolean).length === 0}>
            {templateSaving ? 'Saving...' : 'Save Template'}
          </button>
          {templateMsg && <span style={{ fontSize: '13px', color: templateMsg.includes('success') ? '#16a34a' : '#dc2626' }}>{templateMsg}</span>}
        </div>

        {/* Current saved mapping */}
        {(viewingTemplate as any).order_template_config?.columns && Object.keys((viewingTemplate as any).order_template_config.columns).length > 0 && !templateColumns.length && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#475569' }}>Currently Saved Mapping:</h4>
            <div className="form-grid-2" style={{ fontSize: '12px', gap: '4px', marginBottom: 0 }}>
              {Object.entries((viewingTemplate as any).order_template_config.columns).map(([key, val]) => (
                <div key={key}><strong>{key}:</strong> {val as string}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Integration Config View
  if (viewingIntegration) {
    const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', width: '100%', background: 'white' };
    const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' };

    return (
      <div>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setViewingIntegration(null)}>← Back</button>
            <h2 className="card-title" style={{ margin: 0 }}>Integration Config — {viewingIntegration.name}</h2>
          </div>
        </div>

        {integrationLoading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
        ) : (
          <div style={{ padding: '20px' }}>
            {/* Status Bar */}
            {integrationConfig && (
              <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                <div style={{ padding: '12px', background: integrationConfig.last_order_sync_status === 'failed' ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${integrationConfig.last_order_sync_status === 'failed' ? '#fca5a5' : '#bbf7d0'}` }}>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Last Order Sync</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: integrationConfig.last_order_sync_status === 'failed' ? '#dc2626' : '#16a34a' }}>
                    {integrationConfig.last_order_sync_status || 'Never'} {integrationConfig.last_order_sync_at ? `— ${new Date(integrationConfig.last_order_sync_at).toLocaleString('en-IN')}` : ''}
                  </div>
                  {integrationConfig.last_order_sync_message && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{integrationConfig.last_order_sync_message}</div>}
                </div>
                <div style={{ padding: '12px', background: integrationConfig.last_status_push_status === 'failed' ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${integrationConfig.last_status_push_status === 'failed' ? '#fca5a5' : '#bbf7d0'}` }}>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Last Status Push</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: integrationConfig.last_status_push_status === 'failed' ? '#dc2626' : '#16a34a' }}>
                    {integrationConfig.last_status_push_status || 'Never'} {integrationConfig.last_status_push_at ? `— ${new Date(integrationConfig.last_status_push_at).toLocaleString('en-IN')}` : ''}
                  </div>
                  {integrationConfig.last_status_push_message && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{integrationConfig.last_status_push_message}</div>}
                </div>
              </div>
            )}

            {/* Config Form */}
            <div className="form-grid-2" style={{ gap: '16px', marginBottom: '20px' }}>
              <div className="span-2">
                <label style={labelStyle}>API Base URL</label>
                <input type="text" placeholder="https://sf-newhirekit.logogear.co.in" value={integrationForm.api_base_url} onChange={(e) => setIntegrationForm({ ...integrationForm, api_base_url: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>API Email {integrationConfig?.has_credentials && <span style={{ color: '#16a34a', fontSize: '10px' }}>(credentials saved)</span>}</label>
                <input type="email" placeholder="Leave blank to keep existing" value={integrationForm.email} onChange={(e) => setIntegrationForm({ ...integrationForm, email: e.target.value })} style={inputStyle} autoComplete="off" />
              </div>
              <div>
                <label style={labelStyle}>API Password</label>
                <input type="password" placeholder="Leave blank to keep existing" value={integrationForm.password} onChange={(e) => setIntegrationForm({ ...integrationForm, password: e.target.value })} style={inputStyle} autoComplete="new-password" />
              </div>
              <div className="span-2">
                <label style={labelStyle}>Program IDs (comma-separated, leave empty to sync all programs)</label>
                <input type="text" placeholder="e.g. abc123, def456" value={integrationForm.program_ids} onChange={(e) => setIntegrationForm({ ...integrationForm, program_ids: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Order Sync From Date</label>
                <input type="date" value={integrationForm.order_sync_from_date} onChange={(e) => setIntegrationForm({ ...integrationForm, order_sync_from_date: e.target.value })} style={inputStyle} />
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>First sync imports orders from this date. After that, syncs from last successful sync time.</div>
              </div>
              <div>
                <label style={labelStyle}>Order Import Schedule</label>
                <select value={integrationForm.order_sync_schedule} onChange={(e) => setIntegrationForm({ ...integrationForm, order_sync_schedule: e.target.value })} style={inputStyle}>
                  <option value="hourly">Every Hour</option>
                  <option value="every_3h">Every 3 Hours</option>
                  <option value="manual">Manual Only</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Status Push Schedule</label>
                <select value={integrationForm.status_push_schedule} onChange={(e) => setIntegrationForm({ ...integrationForm, status_push_schedule: e.target.value })} style={inputStyle}>
                  <option value="every_3h">Every 3 Hours</option>
                  <option value="hourly">Every Hour</option>
                  <option value="manual">Manual Only</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Sync Start Hour (IST)</label>
                <input type="number" min={0} max={23} value={integrationForm.sync_start_hour} onChange={(e) => setIntegrationForm({ ...integrationForm, sync_start_hour: parseInt(e.target.value) || 6 })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Sync End Hour (IST)</label>
                <input type="number" min={0} max={23} value={integrationForm.sync_end_hour} onChange={(e) => setIntegrationForm({ ...integrationForm, sync_end_hour: parseInt(e.target.value) || 23 })} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={integrationForm.sync_enabled} onChange={(e) => setIntegrationForm({ ...integrationForm, sync_enabled: e.target.checked })} id="sync-enabled" />
                <label htmlFor="sync-enabled" style={{ fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Enable Auto-Sync</label>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={handleSaveIntegration} disabled={integrationSaving} style={{ fontSize: '13px' }}>
                {integrationSaving ? 'Saving...' : 'Save Config'}
              </button>
              <button className="btn btn-secondary" onClick={handleTestConnection} disabled={testingConnection} style={{ fontSize: '13px' }}>
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </button>
              <button className="btn btn-secondary" onClick={() => handleManualSync('import')} disabled={manualSyncing} style={{ fontSize: '13px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>
                {manualSyncing ? 'Running...' : 'Import Orders Now'}
              </button>
              <button className="btn btn-secondary" onClick={() => handleManualSync('push')} disabled={manualSyncing} style={{ fontSize: '13px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                {manualSyncing ? 'Running...' : 'Push Statuses Now'}
              </button>
              <button className="btn btn-secondary" onClick={handleResetSync} style={{ fontSize: '13px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5' }}>
                Reset Sync
              </button>
            </div>

            {/* Test Connection Result */}
            {testResult && (
              <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: testResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${testResult.success ? '#bbf7d0' : '#fca5a5'}` }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: testResult.success ? '#166534' : '#991b1b' }}>{testResult.success ? 'Connection Successful' : 'Connection Failed'}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{testResult.message}</div>
                {testResult.programs && testResult.programs.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px' }}>
                    <strong>Programs found:</strong> {testResult.programs.map((p: any) => p.name || p.id).join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Sync Logs */}
            {syncLogs.length > 0 && (
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Recent Sync Logs</h4>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafb' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Type</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Status</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Started</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncLogs.map((log: any) => (
                        <tr key={log.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 10px' }}>{log.sync_type === 'order_import' ? 'Order Import' : 'Status Push'}</td>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                              background: log.status === 'success' ? '#d4edda'
                                : log.status === 'failed' ? '#f8d7da'
                                : log.status === 'skipped' ? '#e2e8f0'
                                : '#fff3cd',
                              color: log.status === 'success' ? '#155724'
                                : log.status === 'failed' ? '#721c24'
                                : log.status === 'skipped' ? '#475569'
                                : '#856404',
                            }}>
                              {log.status === 'skipped' ? 'skipped (not due)' : log.status}
                            </span>
                          </td>
                          <td style={{ padding: '6px 10px', color: '#64748b' }}>{log.started_at ? new Date(log.started_at).toLocaleString('en-IN') : ''}</td>
                          <td style={{ padding: '6px 10px', color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.orders_imported > 0 && `Imported: ${log.orders_imported} `}
                            {log.orders_skipped > 0 && `Skipped: ${log.orders_skipped} `}
                            {log.statuses_pushed > 0 && `Pushed: ${log.statuses_pushed} `}
                            {log.error_message || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // SKU Mapping View
  if (viewingMappings) {
    return (
      <div>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setViewingMappings(null)}>← Back</button>
            <h2 className="card-title" style={{ margin: 0 }}>SKU Mappings — {viewingMappings.name} <span style={{ fontFamily: 'monospace', color: '#64748b', fontSize: '14px' }}>({(viewingMappings as any).code})</span></h2>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={mappingSearch}
              onChange={(e) => { setMappingSearch(e.target.value); fetchMappings(viewingMappings.id, e.target.value); }}
              placeholder="Search SKU or product..."
              style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '250px' }}
            />
            {isAdmin && (
              <>
                <button className="btn btn-primary btn-sm" onClick={() => setShowAddMapping(true)}>+ Add Mapping</button>
              </>
            )}
          </div>
        </div>

        <div className="table-container">
          {mappingLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
          ) : mappings.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No SKU mappings found for this channel.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Channel SKU</th>
                  <th>Nexus SKU</th>
                  <th>Product Name</th>
                  <th>Channel Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{m.channel_sku}</td>
                    <td style={{ fontFamily: 'monospace', color: '#0d9488' }}>{m.nexus_sku}</td>
                    <td>{m.product_name}</td>
                    <td>{m.channel_price != null ? `₹${m.channel_price.toLocaleString('en-IN')}` : '—'}</td>
                    <td>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '3px 8px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}
                            onClick={() => handlePushProduct(m.product_id)}
                            disabled={pushingProductId === m.product_id}
                          >
                            {pushingProductId === m.product_id ? '...' : 'Push'}
                          </button>
                          <button className="btn btn-danger" style={{ fontSize: '11px', padding: '3px 8px' }} onClick={() => handleRemoveMappingClick(m.product_id, m.nexus_sku || m.channel_sku)}>Remove</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add Mapping Modal */}
        {showAddMapping && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: '500px' }}>
              <h3>Add SKU Mapping</h3>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Nexus Product *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={newMappingProductSearch}
                    onChange={(e) => { setNewMappingProductSearch(e.target.value); setNewMappingProduct(null); searchProductsForMapping(e.target.value); }}
                    placeholder="Search by SKU or product name..."
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  />
                  {newMappingResults.length > 0 && !newMappingProduct && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '160px', overflow: 'auto' }}>
                      {newMappingResults.map(p => {
                        const color = p.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
                        const decoration = p.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
                        const label = `${p.sku} - ${p.name}${color ? ` - ${color}` : ''}${decoration ? ` - ${decoration}` : ''}`;
                        return (
                        <div key={p.id} onMouseDown={() => { setNewMappingProduct(p); setNewMappingProductSearch(label); setNewMappingResults([]); setNewMappingChannelSku(p.sku); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                          onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                          onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{p.sku}</span> - {p.name}
                          {color && <span style={{ color: '#64748b' }}> - {color}</span>}
                          {decoration && <span style={{ color: '#64748b' }}> - {decoration}</span>}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Channel SKU</label>
                <input
                  type="text"
                  value={newMappingChannelSku}
                  onChange={(e) => setNewMappingChannelSku(e.target.value)}
                  placeholder="External SKU on this channel"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>If blank, Nexus SKU will be used</div>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Channel Price (optional)</label>
                <input
                  type="number"
                  value={newMappingPrice}
                  onChange={(e) => setNewMappingPrice(e.target.value)}
                  placeholder="Price on this channel"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button className="btn btn-primary" onClick={handleAddMapping} disabled={!newMappingProduct}>Add Mapping</button>
                <button className="btn btn-cancel" onClick={() => { setShowAddMapping(false); setNewMappingProduct(null); setNewMappingProductSearch(''); setNewMappingResults([]); }}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="card-header">
        <h2 className="card-title">Channel Management</h2>
        {isAdmin && (
          <button className="btn btn-primary" onClick={handleOpenCreate}>
            <span className="btn-icon">➕</span>
            New Channel
          </button>
        )}
      </div>

      {/* Channel Table */}
      <div className="table-container">
        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr>
              <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>Code {sortField === 'code' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>Type {sortField === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer' }}>Status {sortField === 'is_active' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>Created {sortField === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedChannels.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No channels found. {isAdmin ? 'Create your first channel.' : ''}
                </td>
              </tr>
            ) : (
              sortedChannels.map((channel) => (
                <tr key={channel.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '14px' }}>{(channel as any).code || '—'}</td>
                  <td style={{ fontWeight: 600 }}>{channel.name}</td>
                  <td>{getTypeBadge(channel.type)}</td>
                  <td>
                    {isAdmin ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={channel.is_active}
                          onChange={() => handleToggleActive(channel)}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span className={`status-badge ${channel.is_active ? 'status-active' : 'status-inactive'}`}
                          style={{ fontSize: '11px', padding: '2px 8px' }}>
                          {channel.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    ) : (
                      <span className={`status-badge ${channel.is_active ? 'status-active' : 'status-inactive'}`}
                        style={{ fontSize: '11px', padding: '2px 8px' }}>
                        {channel.is_active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '13px', color: '#64748b' }}>
                    {channel.created_at ? new Date(channel.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="btn btn-primary" onClick={() => handleViewMappings(channel)}
                          style={{ minWidth: '80px' }}>
                          Mappings
                        </button>
                        <button className="btn btn-primary" onClick={() => handleOpenTemplate(channel)}
                          style={{ minWidth: '80px' }}>
                          Template
                        </button>
                        <button className="btn btn-primary" onClick={() => handleOpenIntegration(channel)}
                          style={{ minWidth: '80px', background: '#6366f1' }}>
                          Sync
                        </button>
                        <button className="btn btn-secondary" onClick={() => handleOpenEdit(channel)}
                          style={{ minWidth: '60px' }}>
                          Edit
                        </button>
                        <button className="btn btn-danger" onClick={() => handleDeleteClick(channel.id)}
                          style={{ minWidth: '70px' }}>
                          Delete
                        </button>
                      </div>
                    )}
                    {!isAdmin && (
                      <button className="btn btn-primary" onClick={() => handleViewMappings(channel)}
                        style={{ minWidth: '80px' }}>
                        Mappings
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <h3>{editingChannel ? 'Edit Channel' : 'Create Channel'}</h3>

            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Amazon India"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Code * <span style={{ fontWeight: 400, fontSize: '12px', color: '#64748b' }}>(5 characters, e.g. AMZON, FLPKT, WEBST)</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().slice(0, 5) })}
                placeholder="AMZON"
                maxLength={5}
                style={{ width: '100px', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontFamily: 'monospace', fontSize: '16px', textTransform: 'uppercase', letterSpacing: '2px' }}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              >
                {CHANNEL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span style={{ fontWeight: 600 }}>Active</span>
              </label>
            </div>

            <div style={{ marginTop: '1rem', padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#334155' }}>Default Package Dimensions & Weight</div>
              <div className="form-grid-3">
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Length (cm)</label>
                  <input type="number" step="0.1" value={formData.default_length} onChange={(e) => setFormData({ ...formData, default_length: e.target.value })} placeholder="L" style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Width (cm)</label>
                  <input type="number" step="0.1" value={formData.default_width} onChange={(e) => setFormData({ ...formData, default_width: e.target.value })} placeholder="W" style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Height (cm)</label>
                  <input type="number" step="0.1" value={formData.default_height} onChange={(e) => setFormData({ ...formData, default_height: e.target.value })} placeholder="H" style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                </div>
              </div>
              <div className="form-grid-3" style={{ marginTop: '8px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Actual Weight (kg)</label>
                  <input type="number" step="0.01" value={formData.default_weight} onChange={(e) => setFormData({ ...formData, default_weight: e.target.value })} placeholder="kg" style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Declared Value (₹)</label>
                  <input type="number" step="1" value={formData.default_declared_value} onChange={(e) => setFormData({ ...formData, default_declared_value: e.target.value })} placeholder="₹" style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>Volumetric Weight (kg)</label>
                  <div style={{ padding: '6px 8px', background: '#e2e8f0', borderRadius: '4px', fontSize: '13px', fontWeight: 600, color: '#0d9488' }}>
                    {formData.default_length && formData.default_width && formData.default_height
                      ? (parseFloat(formData.default_length) * parseFloat(formData.default_width) * parseFloat(formData.default_height) / 5000).toFixed(2) + ' kg'
                      : '—'}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Formula: (L × W × H) / 5000. Used as default in order exports for this channel.</div>
            </div>

            {/* Auto-allocation setting */}
            <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={formData.auto_allocate}
                  onChange={(e) => setFormData({ ...formData, auto_allocate: e.target.checked })}
                />
                <div>
                  <span style={{ fontWeight: 600, color: '#166534' }}>Auto-allocate orders</span>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                    Orders imported from this channel will be automatically allocated (stock reserved) on creation.
                  </div>
                </div>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editingChannel ? 'Update Channel' : 'Create Channel'}
              </button>
              <button className="btn btn-secondary" onClick={handleCloseForm} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>This will delete the channel and remove all product associations. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Channel
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteId(null);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={removeMappingConfirm.show}
        title="Remove SKU Mapping"
        message="Remove this SKU mapping from the channel?"
        itemName={removeMappingConfirm.name}
        confirmLabel="Remove"
        onConfirm={handleRemoveMapping as any}
        onCancel={() => setRemoveMappingConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default ChannelManager;
